import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NoteBlank } from 'phosphor-react';
import Header from '../components/Header';
import SearchField from '../components/SearchField';
import CustomerTile from '../components/CustomerTile';
import EditCustomerDialog from '../components/EditCustomerDialog';
import { useApi } from '../hooks/useApi';
import { Conversation } from '../types/api';
import { Skeleton } from '../components/Skeleton';
import { PageNavBar } from '../components/PageNavBar';

interface Customer {
  phone: string;
  company?: string;
  contactName?: string;
  conversationIds?: number[]; // Array of conversation IDs for this phone number
}

interface ClientsProps {
  onBack?: () => void;
  onMenuClick?: () => void;
  onCustomerClick?: (customer: Customer) => void;
}

export const Clients: React.FC<ClientsProps> = ({ onBack, onMenuClick, onCustomerClick }) => {
  const api = useApi();
  const [searchValue, setSearchValue] = useState('');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);

  // Fetch conversations from API to extract customer data
  useEffect(() => {
    // Don't fetch if authentication is still loading or user is not authenticated
    if (api.isLoading || !api.isAuthenticated) {
      return;
    }

    const fetchCustomers = async () => {
      try {
        setIsLoading(true);
        console.log('Clients: Starting to fetch conversations...');
        const apiConversations = await api.getAllConversations();
        console.log('Clients: Received conversations from API:', apiConversations);
        
        setConversations(apiConversations);
        setError(null);
        console.log('Clients: Conversations loaded successfully');
      } catch (err) {
        console.error('Clients: Error fetching conversations:', err);
        setError('Error al cargar los clientes. Por favor, intenta nuevamente.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCustomers();
  }, [api, api.isAuthenticated, api.isLoading]);

  // Transform conversations into unique customers (all phone numbers that have interacted)
  const customers = useMemo(() => {
    const customerMap = new Map<string, Customer>();
    
    conversations.forEach((conv) => {
      const phone = conv.customerPhone || '';
      if (!phone) return;
      
      const normalizedPhone = phone.startsWith('+') ? phone : `+${phone}`;
      
      // If we already have this customer, merge information
      if (customerMap.has(normalizedPhone)) {
        const existing = customerMap.get(normalizedPhone)!;
        // Update with more complete information if available
        if (!existing.company && conv.context?.company) {
          existing.company = conv.context.company;
        }
        if (!existing.contactName && conv.customerName) {
          existing.contactName = conv.customerName;
        }
        // Add conversation ID to the array
        if (conv.id && !existing.conversationIds?.includes(conv.id)) {
          existing.conversationIds = [...(existing.conversationIds || []), conv.id];
        }
      } else {
        // Create new customer entry
        customerMap.set(normalizedPhone, {
          phone: normalizedPhone,
          company: conv.context?.company || undefined,
          contactName: conv.customerName || undefined,
          conversationIds: conv.id ? [conv.id] : []
        });
      }
    });
    
    return Array.from(customerMap.values()).sort((a, b) => {
      // Sort by contact name if available, otherwise by phone
      const aName = a.contactName || a.company || a.phone;
      const bName = b.contactName || b.company || b.phone;
      return aName.localeCompare(bName);
    });
  }, [conversations]);

  // Filter by search
  const filteredCustomers = customers.filter(customer => {
    const searchLower = searchValue.toLowerCase();
    const phone = customer.phone || '';
    const company = customer.company || '';
    const contactName = customer.contactName || '';
    
    return (
      phone.includes(searchLower) ||
      company.toLowerCase().includes(searchLower) ||
      contactName.toLowerCase().includes(searchLower)
    );
  });

  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
  };

  const handleSaveCustomer = async (company: string, contactName: string) => {
    if (!editingCustomer) return;

    try {
      // TODO: Implementar actualización en el backend
      // Por ahora, actualizamos el estado local
      // En el futuro, esto debería hacer una llamada a la API para actualizar las conversaciones
      console.log('Guardar cliente:', {
        phone: editingCustomer.phone,
        company,
        contactName
      });

      // Actualizar el estado local de conversaciones
      setConversations(prev => prev.map(conv => {
        if (conv.customerPhone === editingCustomer.phone || 
            `+${conv.customerPhone}` === editingCustomer.phone) {
          return {
            ...conv,
            customerName: contactName || conv.customerName,
            context: {
              ...conv.context,
              company: company || conv.context?.company
            }
          };
        }
        return conv;
      }));

      setEditingCustomer(null);
    } catch (err) {
      console.error('Error al guardar cliente:', err);
      setError('Error al guardar los datos del cliente');
    }
  };

  const emptyState = (
    <motion.div 
      className="flex flex-col items-center justify-center py-12 text-text-subtle w-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.3 }}
        style={{ display: 'flex', marginBottom: '0.75rem' }}
      >
        <NoteBlank size={24} weight="bold" color="#646660" style={{ width: '24px', height: '24px', display: 'block', flexShrink: 0 }} />
      </motion.div>
      <motion.p
        initial={{ y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.3 }}
      >
        {searchValue.trim() 
          ? `No hay clientes para tu búsqueda "${searchValue}"`
          : 'No hay clientes disponibles'
        }
      </motion.p>
    </motion.div>
  );

  return (
    <div
      className="bg-[#eceee9] flex flex-col relative w-full min-h-screen"
      data-name="clientes"
      data-node-id="17:1535"
    >
      {/* Header */}
      <Header className="shrink-0 w-full" onMenuClick={onMenuClick} />

      <PageNavBar
        onBack={onBack}
        title="Clientes"
        className="bg-[#eceee9]"
      />

      {/* Content container with max-width */}
      <div className="flex flex-col items-center w-full">
        <div className="flex flex-col w-full max-w-[1280px] px-3">
          {/* Search Field */}
          <div className="bg-[#eceee9] flex flex-col gap-2 items-start pb-2 pt-6 shrink-0 w-full">
            <div className="w-full max-w-[480px]">
              <SearchField
                placeholder="Buscar"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </div>
          </div>

          {/* Customers List */}
          <div className="flex-1 flex py-2 relative w-full min-h-0">
            <div className="flex-1 bg-white flex flex-col items-start p-2 rounded-lg w-full overflow-y-auto">
              {isLoading ? (
                <div className="flex flex-col gap-2 w-full">
                  {[...Array(6)].map((_, index) => (
                    <motion.div 
                      key={index} 
                      className="p-3 w-full"
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05, duration: 0.4 }}
                    >
                      <div className="flex gap-3 items-start">
                        <div className="flex-1 flex flex-col gap-2">
                          <Skeleton width="150px" height="20px" />
                          <Skeleton width="200px" height="18px" />
                          <Skeleton width="180px" height="18px" />
                        </div>
                        <Skeleton width="80px" height="40px" className="rounded-md" />
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : error ? (
                <motion.div 
                  className="text-center py-8 text-red-600 w-full"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <p>{error}</p>
                  <p className="text-xs mt-2">Por favor, abre la consola (F12) para ver más detalles del error.</p>
                </motion.div>
              ) : filteredCustomers.length > 0 ? (
                <AnimatePresence>
                  {filteredCustomers.map((customer, index) => (
                    <motion.div
                      key={customer.phone}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ 
                        duration: 0.5, 
                        delay: index * 0.02,
                        ease: [0.25, 0.46, 0.45, 0.94]
                      }}
                      className="w-full"
                    >
                      <CustomerTile
                        phone={customer.phone}
                        company={customer.company}
                        contactName={customer.contactName}
                        onEdit={() => handleEdit(customer)}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              ) : (
                emptyState
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Customer Dialog */}
      {editingCustomer && (
        <EditCustomerDialog
          isOpen={!!editingCustomer}
          onClose={() => setEditingCustomer(null)}
          phone={editingCustomer.phone}
          company={editingCustomer.company}
          contactName={editingCustomer.contactName}
          onSave={handleSaveCustomer}
        />
      )}
    </div>
  );
};

export default Clients;

